import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import type { Offer } from '@psp-aggregator/sdk';

import { getOfferAttributes } from '../../libs/offerAttributes';
import { legMethodLabel } from '../../libs/offerDraft';
import {
  formatLegMargin,
  formatLegRate,
  legCost,
  legMargin,
  legRateUnit,
  legSell,
} from '../../libs/offerRates';
import { OFFER_LEG_I18N_KEY, type OfferLegKey } from '../../model/legs';

const RATE_LABEL = {
  cost: {
    fixed: 'pages.admin.offers.fields.costFixed',
    percent: 'pages.admin.offers.fields.costPct',
  },
  sell: {
    fixed: 'pages.admin.offers.fields.sellFixed',
    percent: 'pages.admin.offers.fields.sellPct',
  },
} as const;

export interface LegBlockProps {
  layout?: 'line' | 'panel';
  leg: OfferLegKey;
  offer: Offer;
  showCost?: boolean;
}

export const LegBlock: FC<LegBlockProps> = ({
  layout = 'line',
  leg,
  offer,
  showCost = false,
}) => {
  const { t } = useTranslation();
  const snapshot = getOfferAttributes(offer).legs[leg];

  if (!snapshot.enabled) {
    return null;
  }

  const sell = formatLegRate(legSell(offer, leg), snapshot.sell_fixed);
  const cost = formatLegRate(legCost(offer, leg), snapshot.cost_fixed);
  const margin = legMargin(offer, leg);
  const costUnit = snapshot.cost_fixed ? 'fixed' : 'percent';
  const sellUnit = snapshot.sell_fixed ? 'fixed' : 'percent';
  const marginText = formatLegMargin(margin, legRateUnit(offer, leg));
  const extra = legMethodLabel(leg, snapshot);
  const title = t(OFFER_LEG_I18N_KEY[leg]);

  if (layout === 'panel') {
    return (
      <div>
        <p className="mb-2 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
          {title}
        </p>
        <div className="space-y-2 rounded-xl border border-border/40 bg-background/60 p-3">
          {!!extra && (
            <div>
              <p className="text-[10px] tracking-wider text-muted-foreground uppercase">
                {t('pages.admin.offers.fields.paymentMethods')}
              </p>
              <p className="mt-0.5 text-sm">{extra}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            {showCost && !!cost && (
              <div>
                <p className="text-[10px] tracking-wider text-muted-foreground uppercase">
                  {t(RATE_LABEL.cost[costUnit])}
                </p>
                <p className="mt-0.5 text-sm tabular-nums">{cost}</p>
              </div>
            )}
            {!!sell && (
              <div>
                <p className="text-[10px] tracking-wider text-muted-foreground uppercase">
                  {t(RATE_LABEL.sell[sellUnit])}
                </p>
                <p className="mt-0.5 text-sm font-medium text-brand tabular-nums">
                  {sell}
                </p>
              </div>
            )}
            {showCost && margin > 0 && (
              <div>
                <p className="text-[10px] tracking-wider text-muted-foreground uppercase">
                  {t('pages.admin.offers.fields.margin')}
                </p>
                <p className="mt-0.5 text-sm font-semibold text-emerald-600 tabular-nums dark:text-emerald-400">
                  {marginText}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!sell && !cost && !extra) {
    return null;
  }

  return (
    <div className="flex items-baseline gap-2 border-t border-border/15 py-1">
      <span className="w-[88px] shrink-0 pt-0.5 text-[10px] font-bold tracking-wider text-muted-foreground uppercase">
        {title}
      </span>
      <p className="min-w-0 flex-1 text-xs leading-snug">
        {showCost && !!cost && (
          <span className="text-muted-foreground">
            {t('pages.admin.offers.fields.costShort')} {cost}
          </span>
        )}
        {showCost && !!cost && !!sell && (
          <span className="text-muted-foreground"> · </span>
        )}
        {!!sell && (
          <span className="font-semibold text-foreground">{sell}</span>
        )}
        {!!extra && <span className="text-muted-foreground"> · {extra}</span>}
        {showCost && margin > 0 && (
          <span className="ml-1.5 rounded-full bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
            {marginText}
          </span>
        )}
      </p>
    </div>
  );
};

LegBlock.displayName = 'LegBlock';
